import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import supportConfig from '../../config/supportConfig.json';
import { sendReportSubmission } from '../../util/api';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { parse } from '../../util/urlHelpers';
import { emailFormatValid, maxLength, required } from '../../util/validators';
import {
  FieldSelect,
  FieldTextInput,
  Form,
  LayoutSingleColumn,
  NamedLink,
  Page,
  PrimaryButton,
} from '../../components';

import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import css from './ReportPage.module.css';

const stringParam = value => (typeof value === 'string' ? value : '');

/**
 * Context-aware trust and safety report page.
 *
 * @component
 * @returns {JSX.Element}
 */
const ReportPage = props => {
  const intl = useIntl();
  const currentUser = useSelector(state => state.user?.currentUser);
  const [submissionState, setSubmissionState] = useState('idle');
  const query = parse(props.location?.search || '');
  const requestedType = stringParam(query.type);
  const contextType = supportConfig.reportContextTypes.includes(requestedType)
    ? requestedType
    : 'general';
  const context = {
    type: contextType,
    id: stringParam(query.id),
    label: stringParam(query.label),
    listingId: stringParam(query.listingId),
    userId: stringParam(query.userId),
    transactionId: stringParam(query.transactionId),
    path: stringParam(query.path) || props.location?.pathname || '/report',
  };
  const currentName = currentUser?.attributes?.profile?.displayName || '';
  const currentEmail = currentUser?.attributes?.email || '';
  const { limits, reportCategories } = supportConfig;
  const requiredMessage = intl.formatMessage({ id: 'ReportPage.required' });
  const validEmailMessage = intl.formatMessage({ id: 'ReportPage.emailInvalid' });
  const tooLong = max => intl.formatMessage({ id: 'ReportPage.tooLong' }, { max });

  const handleSubmit = async values => {
    setSubmissionState('submitting');
    try {
      await sendReportSubmission({
        category: values.category,
        details: values.details,
        name: values.name,
        email: values.email,
        company: values.company,
        context,
      });
      setSubmissionState('success');
    } catch (error) {
      setSubmissionState('error');
    }
  };

  return (
    <Page
      title={intl.formatMessage({ id: 'ReportPage.schemaTitle' })}
      description={intl.formatMessage({ id: 'ReportPage.description' })}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.root} id="main-content">
          <div className={css.content}>
            <header className={css.header}>
              <p className={css.eyebrow}>
                <FormattedMessage id="ReportPage.eyebrow" />
              </p>
              <h1 className={css.heading}>
                <FormattedMessage id="ReportPage.heading" />
              </h1>
              <p className={css.intro}>
                <FormattedMessage id="ReportPage.intro" />{' '}
                <NamedLink name="ContactPage">
                  <FormattedMessage id="ReportPage.supportLink" />
                </NamedLink>
              </p>
            </header>

            {contextType !== 'general' ? (
              <section className={css.context} aria-labelledby="report-context-heading">
                <h2 className={css.contextHeading} id="report-context-heading">
                  <FormattedMessage id="ReportPage.contextHeading" />
                </h2>
                <p className={css.contextText}>
                  <FormattedMessage id={`ReportPage.context.${contextType}`} />
                  {context.label ? `: ${context.label}` : ''}
                </p>
              </section>
            ) : null}

            <FinalForm
              initialValues={{ name: currentName, email: currentEmail }}
              onSubmit={handleSubmit}
              render={({ handleSubmit: submitForm, invalid }) => (
                <Form className={css.form} onSubmit={submitForm}>
                  <FieldTextInput
                    className={css.honeypot}
                    id="report-company"
                    name="company"
                    type="text"
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  <FieldSelect
                    id="report-category"
                    name="category"
                    label={intl.formatMessage({ id: 'ReportPage.categoryLabel' })}
                    required
                    validate={required(requiredMessage)}
                  >
                    <option value="">
                      {intl.formatMessage({ id: 'ReportPage.categoryPlaceholder' })}
                    </option>
                    {reportCategories.map(category => (
                      <option key={category} value={category}>
                        {intl.formatMessage({ id: `ReportPage.category.${category}` })}
                      </option>
                    ))}
                  </FieldSelect>
                  <FieldTextInput
                    id="report-details"
                    name="details"
                    type="textarea"
                    label={intl.formatMessage({ id: 'ReportPage.detailsLabel' })}
                    helpText={intl.formatMessage({ id: 'ReportPage.detailsHelp' })}
                    required
                    maxLength={limits.message}
                    validate={value =>
                      required(requiredMessage)(value) ||
                      maxLength(tooLong(limits.message), limits.message)(value)
                    }
                  />
                  <div className={css.identityFields}>
                    <FieldTextInput
                      id="report-name"
                      name="name"
                      type="text"
                      label={intl.formatMessage({ id: 'ReportPage.nameLabel' })}
                      required
                      maxLength={limits.name}
                      validate={value =>
                        required(requiredMessage)(value) ||
                        maxLength(tooLong(limits.name), limits.name)(value)
                      }
                    />
                    <FieldTextInput
                      id="report-email"
                      name="email"
                      type="email"
                      label={intl.formatMessage({ id: 'ReportPage.emailLabel' })}
                      required
                      maxLength={limits.email}
                      validate={value =>
                        required(requiredMessage)(value) ||
                        emailFormatValid(validEmailMessage)(value) ||
                        maxLength(tooLong(limits.email), limits.email)(value)
                      }
                    />
                  </div>

                  <p className={css.privacyNote}>
                    <FormattedMessage id="ReportPage.privacyNote" />
                  </p>
                  <div className={css.status} aria-live="polite">
                    {submissionState === 'success' ? (
                      <p className={css.success} role="status">
                        <FormattedMessage id="ReportPage.success" />
                      </p>
                    ) : submissionState === 'error' ? (
                      <p className={css.error} role="alert">
                        <FormattedMessage id="ReportPage.error" />
                      </p>
                    ) : null}
                  </div>
                  <PrimaryButton
                    className={css.submitButton}
                    type="submit"
                    disabled={invalid || submissionState === 'submitting'}
                    inProgress={submissionState === 'submitting'}
                  >
                    <FormattedMessage id="ReportPage.submit" />
                  </PrimaryButton>
                </Form>
              )}
            />
          </div>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

export default ReportPage;
